import React, { useState } from "react";
import { AdditionalName } from "../../interfaces/additionalName";
import { createAdditionalName } from "../../services/additionalNameService";
import MonthDayYearPicker from "../component-tools/MonthDayYearPicker";
import { getSafePickerDate, toSafeDateString } from "../../services/helpers";

interface AdditionalNameProps {
  orderID: number;
  onClose: (name?: AdditionalName, message?: string) => void;
}

const AddAdditionalName: React.FC<AdditionalNameProps> = ({
  onClose,
  orderID,
}) => {
  const [additionalName, setAdditionalName] = useState<AdditionalName>({
    additionalNameID: 0,
    firstName: "",
    lastName: "",
    middleName: "",
    fromDate: new Date(),
    toDate: new Date(),
    orderID: orderID,
  });

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadingSpinner, setLoadingSpinner] = useState(false);

  const handleAdditionalNameChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    field: keyof AdditionalName,
  ) => {
    const { value } = e.target;

    setAdditionalName((prevName) => ({
      ...prevName,
      [field]: value,
    }));
  };

  const handleAddAdditionalName = async () => {
    try {
      setLoadingSpinner(true);

      if (!additionalName.lastName) {
        setErrorMessage("Last Name is required.");
        return;
      }

      const nameResponse = await createAdditionalName(additionalName);
      onClose(nameResponse, "Additional Name Created Successfully");
      setErrorMessage("");
    } catch (error) {
      console.error("Error adding additional name:", error);
      setErrorMessage("Failed to add additional name.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  return (
    <>
      <div className="background-mask" onClick={() => onClose()}></div>
      <div className="filter-popup-large">
        <div className="close-button-container">
          <button className="close-button" onClick={() => onClose()}>
            <i className="bi bi-x-circle"></i>
          </button>
        </div>
        <div className="filter-header">
          <h2>Add Additional Name</h2>
        </div>
        <br />
        <div
          className="dialog-container"
          style={{ flexDirection: "row", flexWrap: "wrap" }}
        >
          <div className="field-stacked" style={{ flex: "0 0 40%" }}>
            <label className="field-label">First Name:</label>
            <div className="field-input">
              <input
                className="form-control"
                type="text"
                value={additionalName.firstName || ""}
                onChange={(e) => handleAdditionalNameChange(e, "firstName")}
              />
            </div>
          </div>
          <div className="field-stacked" style={{ flex: "0 0 10%" }}>
            <label className="field-label">Middle Name:</label>
            <div className="field-input">
              <input
                className="form-control"
                type="text"
                value={additionalName.middleName || ""}
                onChange={(e) => handleAdditionalNameChange(e, "middleName")}
              />
            </div>
          </div>
          <div className="field-stacked" style={{ flex: "0 0 40%" }}>
            <label className="field-label">Last Name:</label>
            <div className="field-input">
              <input
                className="form-control"
                type="text"
                value={additionalName.lastName || ""}
                onChange={(e) => handleAdditionalNameChange(e, "lastName")}
                required
              />
            </div>
          </div>

          <div className="field-stacked" style={{ flex: "0 0 70%" }}>
            <label className="field-label">From:</label>
            <MonthDayYearPicker
              value={
                additionalName.fromDate
                  ? getSafePickerDate(additionalName.fromDate)
                  : new Date()
              }
              onChange={(newDate) =>
                handleAdditionalNameChange(
                  { target: { value: toSafeDateString(newDate) } } as any,
                  "fromDate",
                )
              }
            />
          </div>

          <div className="field-stacked" style={{ flex: "0 0 70%" }}>
            <label className="field-label">To:</label>
            <MonthDayYearPicker
              value={
                additionalName.toDate
                  ? getSafePickerDate(additionalName.toDate)
                  : new Date()
              }
              onChange={(newDate) =>
                handleAdditionalNameChange(
                  { target: { value: toSafeDateString(newDate) } } as any,
                  "toDate",
                )
              }
            />
          </div>
        </div>

        <div className="filter-actions">
          <button className="oxbtn btn-green" onClick={handleAddAdditionalName}>
            Save
          </button>
        </div>

        {errorMessage && <div className="error">{errorMessage}</div>}
      </div>

      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </>
  );
};

export default AddAdditionalName;
